import React, { useState } from 'react';
import { useAddApiKey } from '../../hooks/useApiKeys';
import { ApiKeyType } from '../../types';

interface AddApiKeyFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

interface KeyTypeConfig {
  type: ApiKeyType;
  label: string;
  icon: string;
  heading: string;
  instructions: string;
  link: string;
  linkText: string;
  required: boolean;
  placeholder: string;
}

const KEY_TYPE_CONFIGS: KeyTypeConfig[] = [
  {
    type: 'claude',
    label: 'Claude',
    icon: '🤖',
    heading: 'Anthropic Claude API Key',
    instructions: 'Required for AI features including research generation, persona creation, simulations, and evaluations.',
    link: 'https://console.anthropic.com/settings/keys',
    linkText: 'Get your API key from Anthropic Console →',
    required: true,
    placeholder: 'sk-ant-api03-...',
  },
  {
    type: 'tavily',
    label: 'Tavily',
    icon: '🔍',
    heading: 'Tavily Search API Key',
    instructions: 'Required for web search during research generation. Enables gathering market data, competitor analysis, and trend information.',
    link: 'https://tavily.com/',
    linkText: 'Get your API key from Tavily →',
    required: true,
    placeholder: 'tvly-...',
  },
  {
    type: 'gemini',
    label: 'Gemini',
    icon: '✨',
    heading: 'Google Gemini API Key',
    instructions: 'Optional - Enables AI-generated persona avatar images using Google\'s Imagen model.',
    link: 'https://aistudio.google.com/app/apikey',
    linkText: 'Get your API key from Google AI Studio →',
    required: false,
    placeholder: 'AIza...',
  },
];

/**
 * Form for adding a new API key (Claude, Tavily, or Gemini)
 * Validates and tests the key before saving
 */
export const AddApiKeyForm: React.FC<AddApiKeyFormProps> = ({ onSuccess, onCancel }) => {
  const [keyType, setKeyType] = useState<ApiKeyType>('claude');
  const [apiKey, setApiKey] = useState('');
  const [nickname, setNickname] = useState('');
  const [isDefault, setIsDefault] = useState(true);
  const { addKey, loading, error } = useAddApiKey();

  const currentConfig = KEY_TYPE_CONFIGS.find(c => c.type === keyType) || KEY_TYPE_CONFIGS[0];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!apiKey.trim()) {
      return;
    }

    try {
      await addKey(apiKey.trim(), keyType, nickname.trim() || `My ${currentConfig.label} Key`, isDefault);
      onSuccess();
    } catch (err) {
      // Error is handled by the hook
      console.error('Failed to add API key:', err);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Add API Key</h3>
        {currentConfig.required ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Required
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Optional
          </span>
        )}
      </div>

      {/* Key Type Selector */}
      <div>
        <label htmlFor="key-type" className="block text-sm font-medium text-gray-700 mb-2">
          API Key Type <span className="text-red-500">*</span>
        </label>
        <select
          id="key-type"
          value={keyType}
          onChange={(e) => {
            setKeyType(e.target.value as ApiKeyType);
            setApiKey('');
            setNickname('');
          }}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
          disabled={loading}
        >
          {KEY_TYPE_CONFIGS.map(config => (
            <option key={config.type} value={config.type}>
              {config.icon} {config.label}
            </option>
          ))}
        </select>
      </div>

      {/* Contextual Instructions Box */}
      <div className={`border rounded-lg p-4 ${currentConfig.required ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-start">
          <div className="text-2xl mr-3 flex-shrink-0">{currentConfig.icon}</div>
          <div>
            <h4 className={`text-sm font-semibold mb-2 ${currentConfig.required ? 'text-blue-900' : 'text-gray-900'}`}>
              {currentConfig.heading}
            </h4>
            <p className={`text-sm mb-3 ${currentConfig.required ? 'text-blue-700' : 'text-gray-700'}`}>
              {currentConfig.instructions}
            </p>
            <a
              href={currentConfig.link}
              target="_blank"
              rel="noopener noreferrer"
              className={`text-sm font-medium underline ${currentConfig.required ? 'text-blue-800 hover:text-blue-900' : 'text-gray-800 hover:text-gray-900'}`}
            >
              {currentConfig.linkText}
            </a>
          </div>
        </div>
      </div>

      {/* API Key Input */}
      <div>
        <label htmlFor="api-key" className="block text-sm font-medium text-gray-700 mb-2">
          API Key <span className="text-red-500">*</span>
        </label>
        <input
          id="api-key"
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder={currentConfig.placeholder}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors font-mono text-sm"
          disabled={loading}
          required
        />
      </div>

      {/* Nickname Input */}
      <div>
        <label htmlFor="nickname" className="block text-sm font-medium text-gray-700 mb-2">
          Nickname <span className="text-gray-400 text-xs">(Optional)</span>
        </label>
        <input
          id="nickname"
          type="text"
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          placeholder="e.g., Personal, Work, Project X"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
          disabled={loading}
        />
        <p className="mt-2 text-sm text-gray-500">
          A friendly name to help you identify this key
        </p>
      </div>

      {/* Default Checkbox */}
      <div className="flex items-start">
        <div className="flex items-center h-5">
          <input
            id="is-default"
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
            disabled={loading}
          />
        </div>
        <div className="ml-3">
          <label htmlFor="is-default" className="text-sm font-medium text-gray-700">
            Set as default key
          </label>
          <p className="text-sm text-gray-500">
            This key will be used as the default for {currentConfig.label} operations
          </p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-sm font-semibold text-red-900 mb-1">
                Failed to add API key
              </h4>
              <p className="text-sm text-red-700">
                {error.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h4 className="text-sm font-semibold text-yellow-900 mb-1">
              Key Validation
            </h4>
            <p className="text-sm text-yellow-700">
              We'll test your API key to ensure it's valid before saving.
              This may take a few seconds.
            </p>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || !apiKey.trim()}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-500 border border-transparent rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Testing Key...
            </span>
          ) : (
            'Add API Key'
          )}
        </button>
      </div>
    </form>
  );
};
